import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from './icons/Icon';
import './About.css';
import { getPageContent } from '../services/pageContentService';
import { usePageContent } from '../hooks/usePageContent';
import { renderCmsParagraphs } from '../utils/renderCmsText';

const DEFAULT_PORTRAIT = '/images/doctor1.jpg';

const HEADER_DEFAULTS = {
  subtitle: 'About Dr. Muyabin',
  title: 'Your Trusted Dental Care Professional',
  intro: 'With 5 years of dedicated experience in dentistry, Dr. Muyabin is committed to providing exceptional dental care and helping patients achieve their best smiles.',
};

const JOURNEY_DEFAULTS = {
  title: 'Professional Journey',
  content: 'Dr. Muyabin brings a wealth of knowledge and expertise to every patient interaction. With a solid foundation from undergraduate studies at Muhimbili University, Dr. Muyabin combines academic excellence with practical experience to deliver comprehensive dental solutions tailored to each patient\'s unique needs.',
};

const APPROACH_DEFAULTS = {
  title: 'Our Approach',
  content: 'At our practice, we believe in making dentistry a positive experience. Every treatment is approached with care, precision, and a focus on patient comfort. We take the time to understand your concerns, explain procedures clearly, and ensure you feel informed and confident throughout your dental journey.',
};

const REGISTRATION_DEFAULTS = {
  title: 'Professional Registration',
  content: 'Medical Council of Tanzania (MCT)',
};

const About = ({ hideHeader = false }) => {
  const [remotePortrait, setRemotePortrait] = useState(null);
  const [remoteImages, setRemoteImages] = useState(null);

  const { content: header } = usePageContent('about', 'header', HEADER_DEFAULTS);
  const { content: journey } = usePageContent('about', 'journey', JOURNEY_DEFAULTS);
  const { content: approach } = usePageContent('about', 'approach', APPROACH_DEFAULTS);
  const { content: registration } = usePageContent('about', 'registration', REGISTRATION_DEFAULTS);

  const defaultImages = useMemo(() => ([
    { src: DEFAULT_PORTRAIT, alt: 'Dr. Muyabin - Dental Care Professional' },
  ]), []);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const [portraitData, imagesData] = await Promise.all([
          getPageContent('about', 'portrait'),
          getPageContent('about', 'images'),
        ]);
        if (!isMounted) return;

        if (portraitData?.src?.trim()) {
          setRemotePortrait({
            src: portraitData.src.trim(),
            alt: portraitData.alt?.trim() || 'Dr. Muyabin - Dental Care Professional',
          });
        } else {
          setRemotePortrait(null);
        }

        const items = imagesData?.items;
        if (Array.isArray(items)) {
          const enabled = items
            .filter((x) => x && x.enabled !== false)
            .map((x) => ({ src: x.src || '', alt: x.alt || '' }))
            .filter((x) => x.src.trim() && x.alt.trim());
          setRemoteImages(enabled.length ? enabled : []);
        } else {
          setRemoteImages([]);
        }
      } catch {
        if (isMounted) {
          setRemotePortrait(null);
          setRemoteImages([]);
        }
      }
    })();
    return () => { isMounted = false; };
  }, []);

  const images = (remoteImages && remoteImages.length > 0) ? remoteImages : defaultImages;
  const primaryImage = remotePortrait || images[0];

  return (
    <section className="about-section" id="about">
      <div className="about-container">
        {!hideHeader && (
          <div className="about-header">
            <span className="section-eyebrow">{header.subtitle}</span>
            <h2 className="about-title">{header.title}</h2>
            <p className="about-intro">{header.intro}</p>
          </div>
        )}

        <div className="about-profile">
          <div className="about-profile-image">
            <img src={primaryImage.src} alt={primaryImage.alt} loading="lazy" />
            <div className="about-profile-badge">
              <Icon name="award" size={20} />
              <span>MCT Registered</span>
            </div>
          </div>
          <div className="about-profile-card">
            <h3>Dr. Muyabin</h3>
            <p className="about-profile-role">Dentist · Orthodontics (Masters in progress)</p>
            <p>
              Currently pursuing a Master&apos;s in Orthodontics at Muhimbili University of
              Health and Allied Sciences, combining academic excellence with hands-on patient care.
            </p>
            <Link to="/about" className="about-profile-link">
              Read full story
              <Icon name="chevronRight" size={18} />
            </Link>
          </div>
        </div>

        <div className="about-content">
          <div className="about-main">
            <div className="about-text">
              <h3>{journey.title}</h3>
              {renderCmsParagraphs(journey.content)}

              <h3>{approach.title}</h3>
              {renderCmsParagraphs(approach.content)}
            </div>

            <div className="about-qualifications">
              <h3>Education & Qualifications</h3>
              <div className="qualification-item">
                <div className="qualification-icon"><Icon name="graduationCap" size={24} /></div>
                <div className="qualification-details">
                  <h4>Master&apos;s in Orthodontics</h4>
                  <p>Muhimbili University of Health and Allied Sciences</p>
                  <span className="qualification-status">In Progress</span>
                </div>
              </div>
              <div className="qualification-item">
                <div className="qualification-icon"><Icon name="graduationCap" size={24} /></div>
                <div className="qualification-details">
                  <h4>Undergraduate Dental Degree</h4>
                  <p>Muhimbili University of Health and Allied Sciences</p>
                  <span className="qualification-status">Completed</span>
                </div>
              </div>
              <div className="qualification-item">
                <div className="qualification-icon"><Icon name="award" size={24} /></div>
                <div className="qualification-details">
                  <h4>{registration.title}</h4>
                  <p>{registration.content}</p>
                  <span className="qualification-status">Registered</span>
                </div>
              </div>
            </div>
          </div>

          <div className="about-values">
            <h3>Our Core Values</h3>
            <div className="values-grid">
              <div className="value-card">
                <div className="value-icon"><Icon name="gem" size={28} /></div>
                <h4>Excellence</h4>
                <p>Commitment to the highest standards of dental care and professional practice</p>
              </div>
              <div className="value-card">
                <div className="value-icon"><Icon name="heart" size={28} /></div>
                <h4>Compassion</h4>
                <p>Understanding and addressing patient concerns with empathy and care</p>
              </div>
              <div className="value-card">
                <div className="value-icon"><Icon name="microscope" size={28} /></div>
                <h4>Innovation</h4>
                <p>Staying updated with the latest dental techniques and technologies</p>
              </div>
              <div className="value-card">
                <div className="value-icon"><Icon name="handshake" size={28} /></div>
                <h4>Trust</h4>
                <p>Building long-term relationships based on transparency and reliability</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
